import { useState, useCallback, useEffect } from 'react'
import { datadogLogs } from '@datadog/browser-logs'
import { AUTO_BET_CONFIG } from 'src/config'

interface CrashAutoBetState {
  isEnabled: boolean
  isActive: boolean
  autoCashoutEnabled: boolean
  autoCashoutMultiplier: number
}

interface UseCrashAutoBetProps {
  onBet: (amount: string, autoCashoutMultiplier?: number) => Promise<void>
  betAmount: string
  insufficientBalance: boolean
  hasExistingBet: boolean
  isRaceReady: boolean
  playerId: string
  minMultiplier?: number
  maxMultiplier?: number
}

interface StopBettingParams {
  reason?: string
  scope?: string
}

const DEFAULT_AUTO_CASHOUT_MULTIPLIER = 2.0
export const MIN_AUTO_CASHOUT_MULTIPLIER = 1.01
// Uncapped crash (true Aviator/Bustabit): the auto-cash-out target must NOT be
// re-capped at 100x — the engine's crash point is uncapped, so a 100x ceiling
// would silently refuse legitimate high targets (a >100x moon-shot round shows
// its true multiplier, but the player's own auto-target could not express it).
// A large finite ceiling keeps the input sane (no overflow / silly displays)
// while matching the uncap: a higher target is never a payout risk — it simply
// pays min(target, crash), clamped to the operator liability, server-side.
export const MAX_AUTO_CASHOUT_MULTIPLIER = 1_000_000

export const useCrashAutoBet = ({
  onBet,
  betAmount,
  insufficientBalance,
  hasExistingBet,
  isRaceReady,
  playerId,
  minMultiplier = MIN_AUTO_CASHOUT_MULTIPLIER,
  maxMultiplier = MAX_AUTO_CASHOUT_MULTIPLIER
}: UseCrashAutoBetProps) => {
  const [state, setState] = useState<CrashAutoBetState>({
    isEnabled: false,
    isActive: false,
    autoCashoutEnabled: false,
    autoCashoutMultiplier: DEFAULT_AUTO_CASHOUT_MULTIPLIER
  })

  const toggle = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isEnabled: !prev.isEnabled,
      isActive: false
    }))

    datadogLogs.logger.info('crash_auto_bet', {
      user_action: {
        type: 'toggled',
        playerId
      }
    })
  }, [playerId])

  const toggleAutoCashout = useCallback(() => {
    setState((prev) => ({
      ...prev,
      autoCashoutEnabled: !prev.autoCashoutEnabled
    }))

    datadogLogs.logger.info('crash_auto_bet', {
      user_action: {
        type: 'auto_cashout_toggled',
        playerId
      }
    })
  }, [playerId])

  const setAutoCashoutMultiplier = useCallback(
    (multiplier: number) => {
      // Clamp to valid range
      const clamped = Math.max(minMultiplier, Math.min(maxMultiplier, multiplier))
      setState((prev) => ({
        ...prev,
        autoCashoutMultiplier: clamped
      }))
    },
    [minMultiplier, maxMultiplier]
  )

  const startBetting = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isActive: true
    }))

    datadogLogs.logger.info('crash_auto_bet', {
      user_action: {
        type: 'started',
        playerId,
        autoCashoutEnabled: state.autoCashoutEnabled,
        autoCashoutMultiplier: state.autoCashoutMultiplier
      }
    })
  }, [playerId, state.autoCashoutEnabled, state.autoCashoutMultiplier])

  const stopBetting = useCallback(
    ({ reason, scope }: StopBettingParams) => {
      setState((prev) => ({
        ...prev,
        isEnabled: false,
        isActive: false
      }))

      datadogLogs.logger.info('crash_auto_bet', {
        user_action: {
          type: 'stopped',
          scope,
          reason,
          playerId
        }
      })
    },
    [playerId]
  )

  // Handle auto betting when race is ready
  useEffect(() => {
    if (!state.isEnabled || !state.isActive) {
      return
    }

    if (insufficientBalance) {
      stopBetting({
        scope: 'crashBetPanel',
        reason: 'insufficientBalance'
      })
      return
    }

    if (isRaceReady && !hasExistingBet) {
      const timer = setTimeout(() => {
        // Pass auto-cashout multiplier if enabled
        const autoCashout = state.autoCashoutEnabled ? state.autoCashoutMultiplier : undefined
        void onBet(betAmount, autoCashout)
      }, AUTO_BET_CONFIG.BET_COUNTDOWN * 1000)

      datadogLogs.logger.info('crash_auto_bet', {
        user_action: {
          type: 'bet_placed',
          amount: betAmount,
          playerId,
          autoCashoutEnabled: state.autoCashoutEnabled,
          autoCashoutMultiplier: state.autoCashoutEnabled ? state.autoCashoutMultiplier : null
        }
      })

      return () => {
        clearTimeout(timer)
      }
    }
  }, [
    isRaceReady,
    hasExistingBet,
    state.isEnabled,
    state.isActive,
    state.autoCashoutEnabled,
    state.autoCashoutMultiplier,
    insufficientBalance,
    betAmount,
    onBet,
    stopBetting,
    playerId
  ])

  return {
    isEnabled: state.isEnabled,
    isActive: state.isActive,
    autoCashoutEnabled: state.autoCashoutEnabled,
    autoCashoutMultiplier: state.autoCashoutMultiplier,
    toggle,
    toggleAutoCashout,
    setAutoCashoutMultiplier,
    startBetting,
    stopBetting
  }
}
